import { FlaskConical, Pause, Play, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { Titration3D } from './Titration3D';
import { indicatorById } from './titration';
import { MoleMap, TitreChart } from './VolumetricAnalysisVisuals';
import {
  MAX_RUNS,
  MIN_RUNS,
  VOLUMETRIC_CHALLENGES,
  VOLUMETRIC_EXPLORE_SYSTEMS,
  VOLUMETRIC_QUESTION,
  calculateVolumetricExplore,
  exploreSpec,
  volumetricExploreFields,
  type VolumetricExploreResult,
  type VolumetricExploreSetup,
  type VolumetricExploreSystem,
} from './volumetricAnalysisExplore';

const RUN_COUNTS = Array.from({ length: MAX_RUNS - MIN_RUNS + 1 }, (_, index) => MIN_RUNS + index);
const runLabel = (index: number) => (index === 0 ? 'Rough' : `Titre ${index + 1}`);

/**
 * Explore view of Volumetric Analysis (CT-INTERACT): the learner's own standard, aliquot and
 * titres drive the worked calculation, titre chart, mole map and the 3D titration bench.
 */
export function VolumetricAnalysisExplorePanel() {
  const first = VOLUMETRIC_EXPLORE_SYSTEMS[0];
  const [system, setSystem] = useState<VolumetricExploreSystem>(first);
  const [setup, setSetup] = useState<VolumetricExploreSetup>(first.defaults);
  const [result, setResult] = useState<VolumetricExploreResult>(() => calculateVolumetricExplore(first, first.defaults));
  const [rejection, setRejection] = useState<string | null>(null);
  const [run, setRun] = useState<number | null>(null);
  const [playing, setPlaying] = useState(true);

  const apply = (nextSystem: VolumetricExploreSystem, next: VolumetricExploreSetup) => {
    setSetup(next);
    try {
      setResult(calculateVolumetricExplore(nextSystem, next));
      setRejection(null);
    } catch (error) {
      // Fail closed: keep the last valid result and say why the new readings were not used.
      setRejection(error instanceof Error ? error.message : 'These readings are outside the model.');
    }
  };

  const choose = (candidate: VolumetricExploreSystem) => {
    setSystem(candidate);
    setRun(null);
    apply(candidate, candidate.defaults);
  };

  const spec = exploreSpec(system);
  const fields = volumetricExploreFields(system, setup.runs);
  const shownRun = run !== null && run < result.titres.length ? run : result.concordant[0];
  const titre = result.titres[shownRun];
  const aliquotMl = setup.aliquotMl;
  const indicator = indicatorById(system.indicator);
  const flaskMl = aliquotMl + titre;
  const sceneLabel = `Titration bench for ${runLabel(shownRun).toLowerCase()}: the burette delivered ${titre.toFixed(2)} mL of ${spec.titrant.formula} into a ${aliquotMl.toFixed(2)} mL aliquot of ${spec.analyte.formula}, and the flask shows the ${indicator.name} end point.`;

  return (
    <section aria-label="Explore your own titration" className="space-y-6">
      <div className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Choose a reviewed titration</p>
        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Reviewed titrations">
          {VOLUMETRIC_EXPLORE_SYSTEMS.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              aria-pressed={system.id === candidate.id}
              onClick={() => choose(candidate)}
              className={`rounded-full border px-3 py-1.5 text-xs font-extrabold sm:text-sm ${system.id === candidate.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2'}`}
            >
              {candidate.name}
            </button>
          ))}
        </div>
        <p className="mt-3 break-words text-sm font-bold text-ink">{spec.equation}</p>
        <p className="mt-1 text-xs font-bold leading-5 text-slate2">
          {system.method === 'standardise'
            ? `Weigh the primary standard and make it up in a volumetric flask. Pipette an aliquot into the conical flask and titrate with the ${spec.titrant.name} you are standardising.`
            : `Pipette an aliquot of the ${spec.analyte.name} into the conical flask and titrate with ${spec.titrant.name} of known concentration.`}{' '}
          Indicator: {spec.indicator}.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Number of titres">
            <span className="text-sm font-extrabold text-ink">Titre readings</span>
            {RUN_COUNTS.map((count) => (
              <button
                key={count}
                type="button"
                aria-pressed={setup.runs === count}
                onClick={() => apply(system, { ...setup, runs: count })}
                className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${setup.runs === count ? 'border-ink bg-ink text-white' : 'border-hairline bg-surface text-slate2'}`}
              >
                {count} titres
              </button>
            ))}
          </div>
          <ModelInputPanel
            key={system.id}
            legend="Your standard, aliquot and titres"
            fields={fields}
            values={setup}
            onChange={(next) => apply(system, { ...setup, ...next })}
          />
          {rejection ? (
            <p role="alert" className="flex items-start gap-2 rounded-2xl bg-wash-sunshine px-3 py-2 text-sm font-bold text-ink">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{rejection} The model still shows your last valid readings.</span>
            </p>
          ) : null}
          <ExploreChallenges question={VOLUMETRIC_QUESTION} challenges={VOLUMETRIC_CHALLENGES} result={result} />
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="volumetric-step"
            result={`Result: ${result.unknownLabel} = ${result.unknownM.toPrecision(4)} mol/L`}
            resultTestId="volumetric-result"
            limitNote="Each titre must fit one 50.00 mL burette. The model assumes the end point equals the equivalence point and that no side reactions occur."
          />
        </div>

        <div className="min-w-0 space-y-5 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
              <FlaskConical className="h-4 w-4" aria-hidden="true" /> Model view
            </p>
            <button
              type="button"
              onClick={() => setPlaying((current) => !current)}
              className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink"
            >
              {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
              {playing ? 'Pause animation' : 'Play animation'}
            </button>
          </div>
          <Titration3D
            state={{ titrantAddedMl: titre, flaskVolumeMl: flaskMl, indicator: system.indicator, shade: system.endpointShade, dropping: false }}
            playing={playing}
            label={sceneLabel}
          />
          <div className="flex flex-wrap gap-1" role="group" aria-label="Show a titre on the bench">
            {result.titres.map((_, index) => (
              <button
                key={`bench-${index}`}
                type="button"
                aria-pressed={shownRun === index}
                onClick={() => setRun(index)}
                className={`rounded-full border px-2.5 py-1 text-xs font-extrabold ${shownRun === index ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-slate2'}`}
              >
                {runLabel(index)}
              </button>
            ))}
          </div>
          <TitreChart
            revealed
            rows={result.titres.map((value, index) => ({
              label: runLabel(index),
              value,
              rough: index === 0,
              concordant: result.concordant.includes(index),
            }))}
          />
          <div>
            <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate2">Mole map</p>
            <MoleMap steps={result.flow} />
          </div>
        </div>
      </div>
    </section>
  );
}
